import * as React from "react";
import { observer } from "mobx-react";
import { Card, Button, Row, message, Upload, Space } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { CustomFormItem } from "Components/Form/CustomFormItem";
import { ProjectDocumentsTable } from "Components/General/AddFilesInput/ProjectDocumentsTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DraggerProps } from "antd/lib/upload/Dragger";
import { API_URL } from "General/APIEndpoints";
import { API } from "General/API";
import { projectsDataStore } from "Stores/ProjectsStore";
import { SearchFiles } from "./SearchFiles";
import { ModalComponentProps } from "../Modal/ModalContext";
import { uniqBy } from "lodash";
import { FileUploadChunked } from "Pages/Project/Documents/FileUpload/FileUploadChunked";
export type FileUploadType = "ALL" | "EXIST" | "EXTERNAL";
const Dragger = Upload.Dragger;

interface FilesProps extends WithTranslation, ModalComponentProps {
  onChange?: (v: Array<API_FileDataEntity>) => void;
  initialValue: API_FileDataEntity[];
  fileUploadType?: FileUploadType;
  minimumAccessLevel?: API_DOCUMENTS_MINACCESSLEVEL;
  initialTitle?: string;
  isRadioSelection?: boolean;
}

interface FilesState {
  isGhost: {
    external: boolean;
    exist: boolean;
  };
  step: number;
  loading: boolean;
}

export const Files = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<FilesProps, FilesState> {
      constructor(props: FilesProps) {
        super(props);
        this.state = {
          isGhost: {
            external: props.fileUploadType === "ALL" ? true : props.fileUploadType === "EXTERNAL",
            exist: props.fileUploadType === "ALL" ? true : props.fileUploadType === "EXIST",
          },
          step: 0,
          loading: false,
        };
      }

      handleAddAttachments = (data: API_FileDataEntity[]) => {
        if (!data.length) {
          message.error(this.props.t("No files selected"));
        } else {
          this.props.onChange(uniqBy([].concat(data, this.props.initialValue?.slice()).filter(Boolean), "id"));
          this.props.hideModal();
        }
      };

      handleFileUploadTypeChange = (type: "external" | "exist") => {
        if (this.state.isGhost[type]) {
          this.setState({ isGhost: { external: !this.state.isGhost.external, exist: !this.state.isGhost.exist } });
        }
      };

      get draggerProps(): DraggerProps {
        const uploadURL = API_URL.FILE_UPLOAD_V3.uploadTemporaryFile(projectsDataStore.projectId);
        const httpHeader = API.headersObject();

        const draggerProps = {
          onChange: ({ file }) => {
            if (!FileUploadChunked.fileIsNotTooBig(file, true)) {
              return;
            }
            if (!file.id && file.status === "done") {
              const { initialValue = [] } = this.props;
              const files = initialValue.concat(file.response);
              this.props.onChange(files);
            }
            if (file.status === "removed") {
              const { initialValue = [] } = this.props;
              this.props.onChange(
                initialValue.filter((item) => file.response && file.response[0] && file.response[0].id !== item.id)
              );
            }
          },
          beforeUpload: (file) => FileUploadChunked.fileIsNotTooBig(file),
          action: uploadURL,
          showUploadList: true,
          headers: httpHeader,
          multiple: true,
        };
        return draggerProps;
      }

      get steps() {
        const { t } = this.props;
        const renderTitle = (title: string) => {
          return (
            <Row align="middle">
              <span
                onClick={() => {
                  this.setState({ step: this.state.step - 1 });
                }}
              >
                <FontAwesomeIcon
                  icon={["fas", "arrow-left"]}
                  fixedWidth={true}
                  style={{ marginRight: "20px", fontSize: "16px", display: "block" }}
                />
              </span>
              {t(title)}
            </Row>
          );
        };

        return [
          {
            title: (
              <>{this.props.initialTitle || this.props.t(this.props.isRadioSelection ? "Add file" : "Add files")}</>
            ),
            renderContent: this.renderAddFileForm,
          },
          {
            title: <>{t("File search")}</>,
            renderContent: () => (
              <>
                <div className="ant-modal-header" style={{ paddingLeft: 0, paddingRight: 0 }}>
                  <div className="ant-modal-title">{this.props.t("File search")}</div>
                </div>
                <SearchFiles
                  initialValue={this.props.initialValue}
                  onTitleChange={renderTitle}
                  step={this.state.step}
                  handleAddAttachments={this.handleAddAttachments}
                />
              </>
            ),
          },
        ];
      }

      get modalTitle() {
        return this.steps[this.state.step].title;
      }

      renderAddFileForm = () => {
        const { t } = this.props;
        return (
          <>
            <div className="ant-modal-header" style={{ paddingLeft: 0, paddingRight: 0 }}>
              <div className="ant-modal-title">{this.props.initialTitle || this.props.t("Add files")}</div>
            </div>
            {this.props.fileUploadType === "ALL" && (
              <Space.Compact size="large" className="mb-2" style={{ width: "100%" }}>
                <Button
                  style={{ width: "calc(100% / 2)" }}
                  type="primary"
                  ghost={this.state.isGhost.exist}
                  onClick={() => this.handleFileUploadTypeChange("exist")}
                >
                  {t("Existing files")}
                </Button>
                <Button
                  style={{ width: "calc(100% / 2)" }}
                  type="primary"
                  ghost={this.state.isGhost.external}
                  onClick={() => this.handleFileUploadTypeChange("external")}
                >
                  {t("External files")}
                </Button>
              </Space.Compact>
            )}
            <Card variant="borderless" styles={{ root: { boxShadow: "none" }, body: { padding: "24px 0" } }}>
              {this.state.isGhost.exist ? (
                <CustomFormItem fullWidth={true}>
                  <ProjectDocumentsTable
                    initialValue={this.props.initialValue}
                    handleAddAttachments={this.handleAddAttachments}
                    onShowSearch={() => {
                      this.setState({ step: this.state.step + 1 });
                    }}
                    onExit={this.props.hideModal}
                    minimumAccessLevel={this.props.minimumAccessLevel || "VIEW"}
                    isRadioSelection={this.props.isRadioSelection}
                  />
                </CustomFormItem>
              ) : (
                /* FIXME: temporary fix for missing props.children in Dragger */
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                <Dragger {...(this.draggerProps as any)} style={{ minHeight: "180px" }}>
                  <p className="ant-upload-drag-icon">
                    <FontAwesomeIcon icon={["far", "upload"]} size="2x" />
                  </p>
                  <p className="ant-upload-text">
                    {t(`componentUpload:::Click or drag file into this area to upload`)}
                  </p>
                </Dragger>
              )}
            </Card>
          </>
        );
      };

      render() {
        return <>{this.steps[this.state.step].renderContent()}</>;
      }
    }
  )
);
